import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { AdminLayout } from '../../components/admin/AdminLayout';
import { SlideOver } from '../../components/admin/SlideOver';
import { TabNavigation } from '../../components/admin/TabNavigation';
import { Loader2, Facebook, Instagram, Linkedin, Twitter, Calendar, CheckCircle, XCircle, Clock, User, Eye, RefreshCw, AlertCircle, Download, ExternalLink } from 'lucide-react';

interface SocialPost {
  id: string;
  user_id: string;
  user_email: string;
  blog_title: string | null;
  blog_id: string | null;
  platform: string;
  status: string;
  scheduled_at: string | null;
  published_at: string | null;
  created_at: string;
  error_message: string | null;
  retry_count: number;
  last_retry_at: string | null;
  total_likes: number;
  total_comments: number;
  total_shares: number;
}

interface WebhookLog {
  id: string;
  webhook_url: string;
  request_payload: any;
  response_status: number | null;
  response_body: string | null;
  success: boolean;
  error_message: string | null;
  duration_ms: number | null;
  attempt_number: number;
  created_at: string;
}

export function AdminSocialMedia() {
  const navigate = useNavigate();
  const [posts, setPosts] = useState<SocialPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterPlatform, setFilterPlatform] = useState<string>('all');
  const [filterUser, setFilterUser] = useState<string>('all');
  const [activeTab, setActiveTab] = useState<'scheduled' | 'posted' | 'failed'>('scheduled');
  const [selectedPost, setSelectedPost] = useState<SocialPost | null>(null);
  const [webhookLogs, setWebhookLogs] = useState<WebhookLog[]>([]);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [postUrl, setPostUrl] = useState<string | null>(null);
  const [retrying, setRetrying] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [resolving, setResolving] = useState(false);

  useEffect(() => {
    loadPosts();
  }, []);

  async function loadPosts() {
    try {
      const { data, error } = await supabase
        .from('admin_social_media_overview')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(100);

      if (error) throw error;
      setPosts(data || []);
    } catch (err: any) {
      console.error('Error loading social media posts:', err);
    } finally {
      setLoading(false);
    }
  }

  function getPlatformIcon(platform: string) {
    switch (platform) {
      case 'linkedin': return Linkedin;
      case 'facebook': return Facebook;
      case 'instagram': return Instagram;
      case 'x': return Twitter;
      default: return Twitter;
    }
  }

  function getPlatformColor(platform: string) {
    switch (platform) {
      case 'linkedin': return 'text-[#0A66C2]';
      case 'facebook': return 'text-[#1877F2]';
      case 'instagram': return 'text-[#E4405F]';
      default: return 'text-gray-600';
    }
  }

  function getStatusBadge(status: string) {
    switch (status) {
      case 'draft':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700 border border-gray-200">
          Draft
        </span>;
      case 'scheduled':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200 shadow-sm">
          <Clock className="w-3 h-3 mr-1 text-blue-600" />
          Scheduled
        </span>;
      case 'published':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200 shadow-sm">
          <CheckCircle className="w-3 h-3 mr-1 text-emerald-600" />
          Published
        </span>;
      case 'failed':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-50 text-red-700 border border-red-200 shadow-sm">
          <XCircle className="w-3 h-3 mr-1 text-red-600" />
          Failed
        </span>;
      default:
        return null;
    }
  }

  async function loadWebhookLogs(postId: string) {
    setLoadingLogs(true);
    try {
      const { data, error } = await supabase
        .from('social_delivery_log')
        .select('*')
        .eq('post_id', postId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setWebhookLogs(data || []);
    } catch (err: any) {
      console.error('Error loading webhook logs:', err);
    } finally {
      setLoadingLogs(false);
    }
  }

  async function handleCancelPost(postId: string) {
    if (!confirm('Weet je zeker dat je deze geplande post wilt annuleren?')) {
      return;
    }

    setCancelling(true);
    try {
      const { error } = await supabase
        .from('social_media_posts')
        .update({ status: 'draft' })
        .eq('id', postId);

      if (error) throw error;

      alert('Post succesvol geannuleerd!');
      loadPosts();
    } catch (err: any) {
      console.error('Error cancelling post:', err);
      alert(`Fout bij annuleren: ${err.message}`);
    } finally {
      setCancelling(false);
    }
  }

  async function handleRetryPost(postId: string) {
    if (!confirm('Weet je zeker dat je deze post opnieuw wilt proberen te versturen?')) {
      return;
    }

    setRetrying(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/retry-social-media-post`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ post_id: postId }),
        }
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to retry post');
      }

      alert('Post succesvol opnieuw verstuurd!');
      loadPosts();
      if (selectedPost?.id === postId) {
        setSelectedPost(null);
      }
    } catch (err: any) {
      console.error('Error retrying post:', err);
      alert(`Fout bij opnieuw versturen: ${err.message}`);
    } finally {
      setRetrying(false);
    }
  }

  async function handleResolveAlert(postId: string) {
    if (!confirm('Weet je zeker dat je de alert voor deze post wilt markeren als opgelost?')) {
      return;
    }

    setResolving(true);
    try {
      const { error } = await supabase
        .from('cronjob_alerts')
        .update({
          resolved: true,
          resolved_at: new Date().toISOString(),
        })
        .eq('alert_type', 'social_media_failure')
        .eq('metadata->>post_id', postId);

      if (error) throw error;

      alert('Alert succesvol gemarkeerd als opgelost!');
      loadPosts();
    } catch (err: any) {
      console.error('Error resolving alert:', err);
      alert(`Fout bij oplossen alert: ${err.message}`);
    } finally {
      setResolving(false);
    }
  }

  async function handleViewDetails(post: SocialPost) {
    setSelectedPost(post);
    setPostUrl(null);
    loadWebhookLogs(post.id);
    const { data } = await supabase
      .from('social_media_posts')
      .select('post_url')
      .eq('id', post.id)
      .maybeSingle();
    setPostUrl((data?.post_url as string | null) ?? null);
  }

  function downloadLogsJson() {
    if (!selectedPost) return;
    const payload = {
      exported_at: new Date().toISOString(),
      post: { ...selectedPost, post_url: postUrl },
      delivery_logs: webhookLogs,
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `social-post-${selectedPost.id}.json`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  const uniqueUsers = Array.from(new Set(posts.map(p => p.user_email)))
    .sort()
    .map(email => {
      const post = posts.find(p => p.user_email === email);
      return { email, id: post?.user_id || '' };
    });

  const filteredPosts = posts.filter(post => {
    const platformMatch = filterPlatform === 'all' || post.platform === filterPlatform;
    const userMatch = filterUser === 'all' || post.user_email === filterUser;

    let tabMatch = false;
    if (activeTab === 'scheduled') {
      tabMatch = post.status === 'scheduled';
    } else if (activeTab === 'posted') {
      tabMatch = post.status === 'published';
    } else if (activeTab === 'failed') {
      tabMatch = post.status === 'failed';
    }

    return platformMatch && userMatch && tabMatch;
  });

  const stats = {
    total: posts.length,
    draft: posts.filter(p => p.status === 'draft').length,
    scheduled: posts.filter(p => p.status === 'scheduled').length,
    published: posts.filter(p => p.status === 'published').length,
    failed: posts.filter(p => p.status === 'failed').length,
  };

  return (
    <AdminLayout>
      <div className="max-w-7xl mx-auto">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Social Media Posts</h1>
          <p className="text-sm text-gray-600 mt-1">
            Beheer en monitor alle social media posts
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div className="bg-gradient-to-br from-blue-50 to-blue-100/50 rounded-lg shadow-sm border border-blue-200 p-4 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-blue-700 mb-1">Scheduled</p>
                <p className="text-3xl font-bold text-blue-600">{stats.scheduled}</p>
              </div>
              <div className="w-12 h-12 rounded-full bg-blue-500/10 flex items-center justify-center">
                <Clock className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </div>
          <div className="bg-gradient-to-br from-red-50 to-red-100/50 rounded-lg shadow-sm border border-red-200 p-4 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-red-700 mb-1">Failed</p>
                <p className="text-3xl font-bold text-red-600">{stats.failed}</p>
              </div>
              <div className="w-12 h-12 rounded-full bg-red-500/10 flex items-center justify-center">
                <XCircle className="w-6 h-6 text-red-600" />
              </div>
            </div>
          </div>
        </div>

        <TabNavigation
          tabs={[
            { id: 'scheduled', label: 'Scheduled', icon: Clock, count: stats.scheduled },
            { id: 'posted', label: 'Posted', icon: CheckCircle, count: stats.published },
            { id: 'failed', label: 'Failed', icon: XCircle, count: stats.failed }
          ]}
          activeTab={activeTab}
          onTabChange={(tab) => setActiveTab(tab as 'scheduled' | 'posted' | 'failed')}
          color="#E94A47"
        />

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">User</label>
                <select
                  value={filterUser}
                  onChange={(e) => setFilterUser(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-shadow hover:border-gray-400"
                >
                  <option value="all">Alle Gebruikers ({stats.scheduled})</option>
                  {uniqueUsers.map((user) => (
                    <option key={user.id} value={user.email}>
                      {user.email} ({posts.filter(p => p.user_email === user.email && p.status === 'scheduled').length})
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Platform</label>
                <select
                  value={filterPlatform}
                  onChange={(e) => setFilterPlatform(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-shadow hover:border-gray-400"
                >
                  <option value="all">All Platforms</option>
                  <option value="linkedin">LinkedIn</option>
                  <option value="facebook">Facebook</option>
                  <option value="instagram">Instagram</option>
                </select>
              </div>
            </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
          </div>
        ) : filteredPosts.length === 0 ? (
          <div className="bg-gradient-to-br from-gray-50 to-gray-100/50 rounded-lg shadow-sm border border-gray-200 p-12 text-center">
            <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4 shadow-sm">
              <Calendar className="w-8 h-8 text-gray-400" />
            </div>
            <p className="text-gray-600 font-medium">No social media posts found</p>
            <p className="text-sm text-gray-500 mt-1">Try adjusting your filters</p>
          </div>
        ) : (
          <div className="space-y-2">
            {filteredPosts.map((post) => {
              const PlatformIcon = getPlatformIcon(post.platform);
              const platformColor = getPlatformColor(post.platform);

              return (
                <div
                  key={post.id}
                  className="bg-white rounded-lg shadow-sm border border-gray-200 p-3 hover:bg-blue-50/50 hover:border-blue-300 hover:shadow-md transition-all duration-200"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3 flex-1">
                      <PlatformIcon className={`w-5 h-5 ${platformColor}`} />
                      <div className="flex-1">
                        <div className="flex items-center gap-3">
                          <span className="font-medium text-gray-900">
                            {post.platform.charAt(0).toUpperCase() + post.platform.slice(1)}
                          </span>
                          <span className="text-sm text-gray-500">{post.user_email}</span>
                          {getStatusBadge(post.status)}
                        </div>
                        {post.scheduled_at && activeTab === 'scheduled' && (
                          <div className="flex items-center text-sm text-gray-600 mt-1">
                            <Calendar className="w-3.5 h-3.5 mr-1" />
                            {new Date(post.scheduled_at).toLocaleString('nl-NL')}
                          </div>
                        )}
                        {post.published_at && (activeTab === 'posted' || activeTab === 'failed') && (
                          <div className="flex items-center text-sm text-gray-600 mt-1">
                            <CheckCircle className="w-3.5 h-3.5 mr-1" />
                            {new Date(post.published_at).toLocaleString('nl-NL')}
                          </div>
                        )}
                        {post.error_message && (
                          <p className="text-sm text-red-600 mt-1">{post.error_message}</p>
                        )}
                      </div>
                    </div>

                    <div className="flex gap-2 ml-4">
                      {activeTab === 'scheduled' && (
                        <button
                          onClick={() => handleCancelPost(post.id)}
                          disabled={cancelling}
                          className="px-3 py-1.5 text-sm font-medium text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-all hover:scale-105 active:scale-95 disabled:opacity-50"
                        >
                          Annuleer
                        </button>
                      )}

                      {activeTab === 'posted' && (
                        <button
                          onClick={() => handleViewDetails(post)}
                          className="px-3 py-1.5 text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-all hover:scale-105 active:scale-95 flex items-center gap-1"
                        >
                          <Eye className="w-4 h-4" />
                          Bekijk Details
                        </button>
                      )}

                      {activeTab === 'failed' && (
                        <>
                          <button
                            onClick={() => handleViewDetails(post)}
                            className="px-3 py-1.5 text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-all hover:scale-105 active:scale-95 flex items-center gap-1"
                          >
                            <Eye className="w-4 h-4" />
                            Bekijk Details
                          </button>
                          <button
                            onClick={() => handleRetryPost(post.id)}
                            disabled={retrying}
                            className="px-3 py-1.5 text-sm font-medium text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-all hover:scale-105 active:scale-95 disabled:opacity-50 flex items-center gap-1"
                          >
                            <RefreshCw className={`w-4 h-4 ${retrying ? 'animate-spin' : ''}`} />
                            Opnieuw Proberen
                          </button>
                          <button
                            onClick={() => handleResolveAlert(post.id)}
                            disabled={resolving}
                            className="px-3 py-1.5 text-sm font-medium text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 rounded-lg transition-all hover:scale-105 active:scale-95 disabled:opacity-50 flex items-center gap-1"
                          >
                            <AlertCircle className="w-4 h-4" />
                            Markeer als Opgelost
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <SlideOver
          isOpen={selectedPost !== null}
          onClose={() => setSelectedPost(null)}
          title="Social Media Post Details"
        >
          {selectedPost && (
            <div className="p-6 space-y-6">
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="font-semibold text-gray-900 mb-3">Post Information</h3>
                <dl className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <dt className="text-gray-600">Post ID:</dt>
                    <dd className="font-mono text-gray-900">{selectedPost.id}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-gray-600">Platform:</dt>
                    <dd className="font-medium text-gray-900">{selectedPost.platform}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-gray-600">Status:</dt>
                    <dd>{getStatusBadge(selectedPost.status)}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-gray-600">User:</dt>
                    <dd className="text-gray-900">{selectedPost.user_email}</dd>
                  </div>
                  {selectedPost.blog_title && (
                    <div className="flex justify-between">
                      <dt className="text-gray-600">Blog:</dt>
                      <dd className="text-gray-900">{selectedPost.blog_title}</dd>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <dt className="text-gray-600">Retry Count:</dt>
                    <dd className="text-gray-900">{selectedPost.retry_count || 0}</dd>
                  </div>
                  <div className="flex justify-between items-center">
                    <dt className="text-gray-600">Post URL:</dt>
                    <dd>
                      {postUrl ? (
                        <a
                          href={postUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 font-medium text-[#FF6B35] hover:underline"
                        >
                          Bekijk post <ExternalLink className="w-3.5 h-3.5" />
                        </a>
                      ) : (
                        <span className="text-gray-400">Nog geen link</span>
                      )}
                    </dd>
                  </div>
                </dl>
              </div>

              {selectedPost.error_message && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <h3 className="font-semibold text-red-900 mb-2">Error Message</h3>
                  <p className="text-sm text-red-800 font-mono">{selectedPost.error_message}</p>
                </div>
              )}

              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold text-gray-900">Webhook Logs</h3>
                  <button
                    onClick={downloadLogsJson}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <Download className="w-4 h-4" />
                    Download JSON
                  </button>
                </div>
                {loadingLogs ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                  </div>
                ) : webhookLogs.length === 0 ? (
                  <p className="text-sm text-gray-500 py-4">Geen webhook logs gevonden</p>
                ) : (
                  <div className="space-y-4">
                    {webhookLogs.map((log) => (
                      <div
                        key={log.id}
                        className={`border rounded-lg p-4 shadow-sm ${
                          log.success ? 'border-emerald-200 bg-emerald-50' : 'border-red-200 bg-red-50'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium">
                            Attempt #{log.attempt_number}
                          </span>
                          <span
                            className={`text-xs font-semibold px-2 py-1 rounded ${
                              log.success ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'
                            }`}
                          >
                            {log.success ? 'SUCCESS' : 'FAILED'}
                          </span>
                        </div>
                        <dl className="space-y-1 text-xs">
                          <div className="flex justify-between">
                            <dt className="text-gray-600">Time:</dt>
                            <dd className="text-gray-900">
                              {new Date(log.created_at).toLocaleString('nl-NL')}
                            </dd>
                          </div>
                          {log.response_status && (
                            <div className="flex justify-between">
                              <dt className="text-gray-600">Status Code:</dt>
                              <dd className="text-gray-900">{log.response_status}</dd>
                            </div>
                          )}
                          {log.duration_ms && (
                            <div className="flex justify-between">
                              <dt className="text-gray-600">Duration:</dt>
                              <dd className="text-gray-900">{log.duration_ms}ms</dd>
                            </div>
                          )}
                          {log.webhook_url && (
                            <div className="mt-2">
                              <dt className="text-gray-600 mb-1">Webhook URL:</dt>
                              <dd className="text-gray-900 font-mono text-xs bg-white p-2 rounded border border-gray-200 break-all">
                                {log.webhook_url}
                              </dd>
                            </div>
                          )}
                          {log.request_payload && (
                            <div className="mt-2">
                              <dt className="text-gray-600 mb-1">Request Payload:</dt>
                              <dd className="text-gray-900 font-mono text-xs bg-white p-2 rounded border border-gray-200 overflow-x-auto">
                                <pre>{JSON.stringify(log.request_payload, null, 2)}</pre>
                              </dd>
                            </div>
                          )}
                          {log.response_body && (
                            <div className="mt-2">
                              <dt className="text-gray-600 mb-1">Response Body:</dt>
                              <dd className="text-gray-900 font-mono text-xs bg-white p-2 rounded border border-gray-200 overflow-x-auto">
                                <pre>
                                  {(() => {
                                    try {
                                      return JSON.stringify(JSON.parse(log.response_body), null, 2);
                                    } catch {
                                      return log.response_body;
                                    }
                                  })()}
                                </pre>
                              </dd>
                            </div>
                          )}
                          {log.error_message && (
                            <div className="mt-2">
                              <dt className="text-gray-600 mb-1">Error:</dt>
                              <dd className="text-red-800 font-mono text-xs bg-white p-2 rounded border border-red-200">
                                {log.error_message}
                              </dd>
                            </div>
                          )}
                        </dl>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </SlideOver>
      </div>
    </AdminLayout>
  );
}
